import React from "react";
import { Space, Tag, Badge, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../context/ThemeContext";
import Table from "../../commonComponents/Table";
import TablePagination from "../../commonComponents/Pagination";
import { UsersTableProps, User } from "./types";
import { ColumnsType } from "antd/es/table";
import { ActionIconButton } from "./Styles";

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onEditRole,
  onDelete,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const columns: ColumnsType<User> = [
    {
      title: "USER",
      key: "user",
      fixed: "left",
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={record.avatar} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.name || record.username || record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "PHONE",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },
    {
      title: "ROLES",
      key: "roles",
      render: (_, record) => {
        if (record.role) {
          return <Tag color="purple">{record.role}</Tag>;
        }
        const roles = record.roles || [];
        if (roles.length === 0) {
          return <Tag color="default">No Role</Tag>;
        }
        return (
          <Space wrap>
            {roles.map((role) => (
              <Tag
                key={role.id}
                color={role.is_system_role ? "purple" : "blue"}
              >
                {role.name}
                {role.is_system_role && " (System)"}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: "is_active",
      key: "status",
      render: (isActive) => (
        <Badge
          status={isActive !== false ? "success" : "default"}
          text={isActive !== false ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <ActionIconButton
            onClick={() => onViewDetails(record)}
            iconColor="#2D6CCA"
          >
            <FontAwesomeIcon icon={faEye} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => onEditRole(record)}
            iconColor="#2D6CCA"
          >
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => onDelete(record)}
            iconColor="#f5222d"
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
          </ActionIconButton>
        </Space>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      onPageSizeChange(size);
    }
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <>
      <Table<User>
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: true, y: 600 }}
        emptyText="No users found"
        emptyDescription="No users found"
        headerBackgroundColor={isDarkMode ? "#1a202c" : "#F2F2F2"}
        removeVerticalBorders={true}
        thPadding="15px 10px"
        tdPadding="10px 10px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight={true}
        theme={isDarkMode ? "dark" : "light"}
      />
      {users.length > 0 && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total records"
          padding="16px 12px 0px 12px"
        />
      )}
    </>
  );
};

export default UsersTable;
